import React from 'react';
import Icon from '../../Components/Icons/Icon';
import { Link } from 'react-router-dom';
import { useSession } from '../../Components/Session';
import newMenu from '../newMenu';
import { classNames } from '../../Services/helpers';
import { useDialogues } from '../../Components/Dialogues';
import useHomeBadge from '../../Data/useHomeBadge';

const RedesignNavSections = ({ onNavigate, mobile = false, pins = null }) => {
    const { confirm, featureUnavailable, roleInsufficient } = useDialogues();
    const {
        logout,
        isAdmin,
        isAdminSwitched,
        adminSwitchBack,
        showDarkMode,
        setShowDarkMode,
        hasFeature,
        isRoleSufficient,
    } = useSession();
    const homeBadge = useHomeBadge();
    const styles = mobile ? mobileStyles : defaultStyles;

    const afterNav = () => {
        onNavigate?.();
    };

    return (
        <>
            {newMenu.map((section) => {
                if (section.admin && !isAdmin(section.admin)) {
                    return null;
                }

                const sectionLocked = section.role && !isRoleSufficient(section.role);
                const items = section.items.filter((item) => mobile || !item.mobileOnly);

                return (
                    <div key={section.title} className={styles.section}>
                        <div
                            className={classNames('flex items-center', styles.header, sectionLocked && 'cursor-pointer')}
                            onClick={sectionLocked ? () => {
                                roleInsufficient(section.role);
                                afterNav();
                            } : null}
                        >
                            <Icon
                                icon={section.icon}
                                size="1x"
                                className={classNames(
                                    'mr-3',
                                    sectionLocked ? 'fill-gray-400 dark:fill-gray-500' : 'fill-gray-800 dark:fill-gray-200',
                                )}
                                badge={section.notifications ? (homeBadge ? true : null) : null}
                            />
                            <h2 className={classNames(
                                'font-bold',
                                styles.title,
                                sectionLocked ? 'text-gray-400 dark:text-gray-500' : 'text-gray-800 dark:text-gray-200',
                            )}>
                                {section.title}
                            </h2>
                            {sectionLocked && (
                                <Icon icon="Lock" size="0.75x" className="ml-2 fill-gray-400 dark:fill-gray-500" />
                            )}
                        </div>

                        {!sectionLocked && (
                            <div className={section.admin ? styles.adminItems : styles.items}>
                                {items.map((item) => {
                                    const featureLocked = item.feature && !hasFeature(item.feature);
                                    const roleLocked = item.role && !isRoleSufficient(item.role);

                                    if (featureLocked || roleLocked) {
                                        return (
                                            <div
                                                key={item.label}
                                                onClick={() => {
                                                    featureLocked ? featureUnavailable(item.feature) : roleInsufficient(item.role);
                                                    afterNav();
                                                }}
                                                className={classNames(
                                                    'transition-colors text-left cursor-pointer flex items-center justify-between',
                                                    styles.item,
                                                    'text-gray-400 dark:text-gray-500',
                                                )}
                                            >
                                                <span>{item.label}</span>
                                                <Icon icon="Lock" size="0.75x" className="fill-gray-400 dark:fill-gray-500" />
                                            </div>
                                        );
                                    }

                                    return (
                                        <div key={item.label} className="flex items-center justify-between gap-2">
                                            <Link
                                                to={item.disabled ? null : item.to}
                                                onClick={item.disabled ? null : afterNav}
                                                className={classNames(
                                                    'block flex-1 transition-colors text-left',
                                                    styles.item,
                                                    item.disabled
                                                        ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100',
                                                )}
                                            >
                                                {item.label}
                                            </Link>
                                            {pins && item.pinnable !== false && !item.disabled && (
                                                <PinButton
                                                    label={item.label}
                                                    isPinned={pins.pinned.includes(item.to)}
                                                    isFull={pins.isFull}
                                                    onClick={() => pins.togglePin(item.to)}
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                                {section.admin && isAdminSwitched && (
                                    <div
                                        className={classNames('block transition-colors text-left text-red-600 cursor-pointer hover:text-red-700', styles.item)}
                                        onClick={() => {
                                            adminSwitchBack();
                                            afterNav();
                                        }}
                                    >
                                        Switch back
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}

            <div className={classNames('border-t border-gray-200 dark:border-gray-700', styles.divider)}></div>

            <div className="space-y-2">
                <div className="flex items-center py-1">
                    <Link to="/more" onClick={afterNav}>
                        <Icon icon="Gear" size="1x" className="fill-gray-800 dark:fill-gray-200 mr-3" />
                        <span className="text-gray-800 dark:text-gray-200">Settings</span>
                    </Link>
                </div>
                <div
                    className="flex items-center py-1 cursor-pointer"
                    onClick={() => setShowDarkMode(!showDarkMode)}
                >
                    <Icon icon={showDarkMode ? 'SunBright' : 'Moon'} size="1x" className="fill-gray-800 dark:fill-gray-200 mr-3" />
                    <span className="text-gray-800 dark:text-gray-200">{showDarkMode ? 'Light mode' : 'Dark mode'}</span>
                </div>
                <div
                    className="flex items-center py-1 cursor-pointer"
                    onClick={() => confirm(
                        { theme: 'danger', title: 'Logout', message: 'Are you sure you want to logout?', confirmLabel: 'Logout' },
                        async () => {
                            await logout();
                            window.location.reload();
                        },
                    )}
                >
                    <Icon icon="ArrowLeftToBracket" size="1x" className="fill-gray-800 dark:fill-gray-200 mr-3" />
                    <span className="text-gray-800 dark:text-gray-200">Log Out</span>
                </div>
            </div>
        </>
    );
};

export default RedesignNavSections;

const PinButton = ({ label, isPinned, isFull, onClick }) => {
    return (
        <button
            type="button"
            className="flex p-1.5 -mr-1.5 disabled:opacity-30"
            disabled={!isPinned && isFull}
            aria-pressed={isPinned}
            aria-label={`${isPinned ? 'Unpin' : 'Pin'} ${label}`}
            onClick={onClick}
        >
            <Icon
                icon="Thumbtack"
                theme={isPinned ? 'solid' : 'light'}
                size="1x"
                className={isPinned ? 'fill-brand' : 'fill-gray-400 dark:fill-gray-500'}
            />
        </button>
    );
};

const defaultStyles = {
    section: 'mb-6',
    header: 'mb-3',
    title: 'text-base',
    items: 'ml-8 space-y-1',
    adminItems: 'ml-8 space-y-2',
    item: 'py-1',
    divider: 'my-4',
};

const mobileStyles = {
    section: 'mb-3',
    header: 'mb-1',
    title: 'text-sm',
    items: 'ml-7',
    adminItems: 'ml-7',
    item: 'py-1.5 text-sm',
    divider: 'my-3',
};
